import { useCallback, useState } from 'react';

import { Link } from 'react-router-dom';

import type {
  ApiRoomChatMessageJSON,
  ApiStreamEmoteJSON,
} from 'mastodon/api_types/rooms';
import { externalLink, tokenizeLinks } from 'mastodon/utils/linkify';

import { MENTION_SPLIT, mentionsAccount } from '../mentions';

interface Props {
  message: ApiRoomChatMessageJSON;
  /** Viewer is the host or a co-host: may pin, delete, and timeout others. */
  canModerate?: boolean;
  /** The room host's account id — the host's messages are off-limits to co-hosts. */
  hostAccountId?: string;
  currentAccountId?: string;
  /** The room host's custom emotes — :shortcode: renders as an inline image. */
  emotes?: ApiStreamEmoteJSON[];
  /**
   * Handles (acct and username, lowercase) of accounts currently in the room.
   * Only these are highlighted as tags — an `@word` that matches nobody here
   * stays plain text rather than pretending to be a person.
   */
  knownHandles?: Set<string>;
  /** The viewer's own acct/username, used to highlight messages tagging them. */
  currentAccountAcct?: string;
  currentAccountUsername?: string;
  onDelete?: (messageId: string) => void;
  onPin?: (messageId: string) => void;
  onTimeout?: (accountId: string, durationSeconds: number) => void;
  /**
   * Ban this account from the host's chat for good (channel-level — it follows
   * the host into every future room). Host only: co-hosts get timeouts, which
   * are room-scoped, because a co-host grant ends with the room and should not
   * be able to leave a permanent mark on someone else's channel.
   */
  onBan?: (accountId: string, displayName: string) => void;
  onReply?: (message: ApiRoomChatMessageJSON) => void;
  /** Scroll the quoted message into view when its quote is pressed. */
  onJumpToMessage?: (messageId: string) => void;
}

// Twitch-style identity colors: every account gets a stable color, applied
// to their username and as a ring around their avatar, so authors are
// distinguishable at a glance. Deterministic from the account id (same
// color for every viewer, every session, no server state). Palette is
// Twitch's classic hue set re-tuned to stay readable on our dark theme.
const CHAT_COLORS = [
  '#FF6B6B', // red
  '#4FC3F7', // blue
  '#66BB6A', // green
  '#FFA726', // orange
  '#BA68C8', // purple
  '#4DD0E1', // cyan
  '#F06292', // pink
  '#FFD54F', // gold
  '#9CCC65', // yellow-green
  '#FF8A65', // coral
  '#7986CB', // indigo
  '#4DB6AC', // sea green
] as const;

// Exported for tests.
export const chatColorFor = (accountId: string): string => {
  let hash = 0;
  for (let i = 0; i < accountId.length; i++) {
    hash = (hash * 31 + accountId.charCodeAt(i)) >>> 0;
  }
  return CHAT_COLORS[hash % CHAT_COLORS.length] as string;
};

// Split on :shortcode: tokens and swap known emotes for inline images.
// Pure text-to-React substitution — no HTML injection surface.
export const renderWithEmotes = (
  text: string,
  emotes: ApiStreamEmoteJSON[],
): React.ReactNode => {
  if (emotes.length === 0 || !text.includes(':')) return text;
  const byCode = new Map(emotes.map((e) => [e.shortcode.toLowerCase(), e]));
  const parts = text.split(/:([a-zA-Z0-9_]{2,30}):/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    // Odd indices are the captured candidate shortcodes.
    if (i % 2 === 1) {
      const emote = byCode.get(part.toLowerCase());
      if (emote) {
        return (
          <img
            key={`${emote.id}-${i}`}
            className='room-chat__emote'
            src={emote.url}
            alt={`:${emote.shortcode}:`}
            title={`:${emote.shortcode}:`}
          />
        );
      }
      return `:${part}:`;
    }
    return part;
  });
};

// Render @handles as tags on top of the emote pass, over ONE stretch of text
// that is known to contain no URL. Mentions are matched against the people
// actually in the room: an unknown @word renders as plain text, so a message
// can never fake a tag of somebody who is not here.
// `offset` keeps React keys unique across the whole message.
const renderMentionsAndEmotes = (
  text: string,
  emotes: ApiStreamEmoteJSON[],
  offset: number,
  knownHandles?: Set<string>,
  selfHandles?: Set<string>,
): React.ReactNode => {
  if (!knownHandles || knownHandles.size === 0 || !text.includes('@'))
    return renderWithEmotes(text, emotes);

  const parts = text.split(MENTION_SPLIT);
  if (parts.length === 1) return renderWithEmotes(text, emotes);

  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const handle = part.slice(1).toLowerCase();
      if (!knownHandles.has(handle)) return part;
      const isSelf = selfHandles?.has(handle) ?? false;
      return (
        <span
          key={`mention-${offset}-${i}`}
          className={`room-chat__mention${isSelf ? ' room-chat__mention--self' : ''}`}
          data-testid='chat-mention'
        >
          {part}
        </span>
      );
    }
    return (
      <span key={`text-${offset}-${i}`}>{renderWithEmotes(part, emotes)}</span>
    );
  });
};

// The room chat body renderer (ticket 79). URLs are found FIRST and the
// mention/emote passes run only over what is left, so neither can chop a URL in
// half — the `@bob` in https://example.com/@bob is part of the address, not a
// tag, and a `:pog:` inside a path is not an emote. Room chat is not E2EE, but
// it is still rendered from plain text in the browser, so this is the only
// place a URL here can become a link. Parsing and anchor attributes are shared
// with the DM/GC renderer (mastodon/utils/linkify) rather than copied, so the
// two surfaces cannot drift apart on the security properties.
export const renderWithMentions = (
  text: string,
  emotes: ApiStreamEmoteJSON[],
  knownHandles?: Set<string>,
  selfHandles?: Set<string>,
): React.ReactNode => {
  const tokens = tokenizeLinks(text);
  // Overwhelmingly the common case: no URL at all, so nothing about how room
  // chat rendered before today changes.
  if (tokens.length === 1 && tokens[0]?.type === 'text')
    return renderMentionsAndEmotes(text, emotes, 0, knownHandles, selfHandles);

  return tokens.map((token) => {
    if (token.type === 'url') {
      // Past the per-message cap: still readable, just not clickable, and
      // deliberately NOT run through the mention/emote passes.
      if (token.overflow)
        return <span key={`u-${token.start}`}>{token.value}</span>;
      return externalLink(token.value, 'room-chat__link', `u-${token.start}`);
    }
    return (
      <span key={`t-${token.start}`}>
        {renderMentionsAndEmotes(
          token.value,
          emotes,
          token.start,
          knownHandles,
          selfHandles,
        )}
      </span>
    );
  });
};

const TIMEOUT_CHOICES: { label: string; seconds: number }[] = [
  { label: '1m', seconds: 60 },
  { label: '10m', seconds: 600 },
  { label: '1h', seconds: 3600 },
];

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * A line the SERVER wrote into chat — currently only "X is now the host" after
 * a hand-over (ticket 69).
 *
 * Its own component rather than a branch inside ChatMessage: an early return
 * before that component's hooks would break the rules of hooks, and a system
 * line shares none of a message's furniture anyway — no avatar, no author
 * colour, no reply, no moderation menu. It is not anybody's message, so
 * nothing about it should be actionable.
 *
 * The text comes from the server, which is also what a client too old to know
 * about `event` renders — so the same words work either way.
 */
export const ChatSystemMessage: React.FC<{
  message: ApiRoomChatMessageJSON;
}> = ({ message }) => {
  // Ticket 71 — a follow thank-you is the one system line that is a link: it
  // names a person, and the point of naming them is that you can go and look at
  // them. Green and bold per the ticket; still not deletable, pinnable or
  // moderatable, because it is the server's line and not the follower's.
  // Ticket 73 — a gift line names the gifter for the same reason, and it is the
  // public credit the buyer paid for, so it is deliberately the loudest line in
  // chat. Still the server's line: not deletable, pinnable or moderatable.
  const isGift = message.event === 'gifted_subs';
  const isFollow =
    message.event === 'followed_host' ||
    message.event === 'followed_host_first';
  const isLink = isFollow || isGift;

  return (
    <div
      className='room-chat__message room-chat__message--system'
      id={`room-chat-message-${message.id}`}
      data-testid='chat-system-message'
    >
      {isLink ? (
        <Link
          to={`/@${message.account.acct}`}
          className={
            isGift
              ? 'room-chat__system-text room-chat__system-text--gift'
              : 'room-chat__system-text room-chat__system-text--follow'
          }
          data-testid={isGift ? 'chat-gift-line' : 'chat-follow-line'}
        >
          {message.content}
        </Link>
      ) : (
        <span className='room-chat__system-text'>{message.content}</span>
      )}
    </div>
  );
};

export const ChatMessage: React.FC<Props> = ({
  message,
  canModerate,
  hostAccountId,
  currentAccountId,
  emotes,
  knownHandles,
  currentAccountAcct,
  currentAccountUsername,
  onDelete,
  onPin,
  onTimeout,
  onBan,
  onReply,
  onJumpToMessage,
}) => {
  const { account, content, created_at, media_attachment } = message;
  const replyTo = message.reply_to ?? null;
  const [showTimeoutMenu, setShowTimeoutMenu] = useState(false);

  const isOwn = currentAccountId != null && account.id === currentAccountId;
  // The host's messages are untouchable by co-hosts (server-enforced too):
  // only the host themselves may delete them, and nobody can time the host out.
  const isHostMessage = hostAccountId != null && account.id === hostAccountId;
  const viewerIsHost =
    hostAccountId != null && currentAccountId === hostAccountId;
  const canDelete =
    !!onDelete &&
    (isOwn || (!!canModerate && (!isHostMessage || viewerIsHost)));
  const canPin = !!onPin && !!canModerate;
  const canTimeout = !!onTimeout && !!canModerate && !isOwn && !isHostMessage;
  // Banning is the host's alone — see the `onBan` prop. `viewerIsHost` already
  // excludes the host's own messages, so !isHostMessage is what stops a host
  // being offered a ban on themselves.
  const canBan = !!onBan && viewerIsHost && !isOwn && !isHostMessage;

  const handleDelete = useCallback(() => {
    onDelete?.(message.id);
  }, [onDelete, message.id]);

  const handlePin = useCallback(() => {
    onPin?.(message.id);
  }, [onPin, message.id]);

  const handleToggleTimeoutMenu = useCallback(() => {
    setShowTimeoutMenu((v) => !v);
  }, []);

  const handleTimeout = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const seconds = Number(e.currentTarget.dataset.seconds);
      setShowTimeoutMenu(false);
      if (seconds > 0) onTimeout?.(account.id, seconds);
    },
    [onTimeout, account.id],
  );

  const handleBan = useCallback(() => {
    setShowTimeoutMenu(false);
    onBan?.(account.id, account.display_name || account.username);
  }, [onBan, account.id, account.display_name, account.username]);

  const handleReply = useCallback(() => {
    onReply?.(message);
  }, [onReply, message]);

  const handleJumpToQuoted = useCallback(() => {
    if (replyTo && !replyTo.deleted) onJumpToMessage?.(replyTo.id);
  }, [onJumpToMessage, replyTo]);

  const identityColor = chatColorFor(account.id);
  const selfHandles = new Set(
    [currentAccountAcct, currentAccountUsername]
      .filter((v): v is string => !!v)
      .map((v) => v.toLowerCase()),
  );
  const tagsMe =
    !isOwn &&
    mentionsAccount(content, currentAccountAcct, currentAccountUsername);

  return (
    <div
      className={`room-chat__message${tagsMe ? ' room-chat__message--tagged' : ''}`}
      id={`room-chat-message-${message.id}`}
      data-testid={tagsMe ? 'chat-message-tagged' : 'chat-message'}
    >
      {/* The quote leads the message, ABOVE the replier's own name, so the
          chat reads "quotee: quoted message" and then "quoter: reply". It sits
          outside the avatar/name row on purpose: inside it, the avatar would
          line up with the quote instead of with the author it belongs to. */}
      {replyTo && (
        <button
          type='button'
          className='room-chat__quote'
          onClick={handleJumpToQuoted}
          title={
            replyTo.deleted
              ? 'The message this replies to was deleted'
              : 'Go to the message this replies to'
          }
          data-testid='chat-quote'
        >
          <span className='room-chat__quote-name'>
            {replyTo.account.display_name || replyTo.account.username}
          </span>
          {/* Its own element rather than text inside the name: the name is
              width-capped and ellipsised, so a long one would eat the colon. */}
          <span className='room-chat__quote-colon'>:</span>
          {/* A real space, so the quote copies and reads aloud as "name: text".
              The gap on screen comes from CSS; a whitespace-only child of a
              flex container is not rendered, so this adds no second gap. */}{' '}
          <span className='room-chat__quote-text'>
            {replyTo.deleted
              ? 'Message deleted'
              : replyTo.content ||
                (replyTo.has_media ? 'Attachment' : 'Message')}
          </span>
        </button>
      )}
      <div className='room-chat__message-row'>
        <img
          className='room-chat__message-avatar'
          src={account.avatar}
          alt={account.display_name || account.username}
          style={{ boxShadow: `0 0 0 2px ${identityColor}` }}
        />
        <div className='room-chat__message-body'>
          <div className='room-chat__message-meta'>
            {/* Ticket 51: the author's name behaves like a name on the home
              feed — a link to their profile, and the anchor the app-wide
              hover card attaches to. `data-hover-card-account` is the same
              attribute LinkedDisplayName uses, so this is the feed's own
              hover card rather than a second one built for chat. */}
            <Link
              to={`/@${account.acct}`}
              className='room-chat__message-name'
              style={{ color: identityColor }}
              title={`@${account.acct}`}
              data-hover-card-account={account.id}
            >
              {account.display_name || account.username}
            </Link>
            <span className='room-chat__message-time'>
              {formatTime(created_at)}
            </span>
            {(canDelete || canPin || canTimeout || canBan) && (
              <span className='room-chat__message-actions'>
                {canPin && (
                  <button
                    type='button'
                    className='room-chat__message-action'
                    onClick={handlePin}
                    title='Pin message'
                    aria-label='Pin message'
                  >
                    📌
                  </button>
                )}
                {(canTimeout || canBan) && (
                  <button
                    type='button'
                    className='room-chat__message-action'
                    onClick={handleToggleTimeoutMenu}
                    title={canBan ? 'Timeout or ban user' : 'Timeout user'}
                    aria-label={canBan ? 'Timeout or ban user' : 'Timeout user'}
                  >
                    ⏱️
                  </button>
                )}
                {canDelete && (
                  <button
                    type='button'
                    className='room-chat__message-action'
                    onClick={handleDelete}
                    title='Delete message'
                    aria-label='Delete message'
                  >
                    🗑️
                  </button>
                )}
              </span>
            )}
            {onReply && !message.deleted && (
              // Always rendered, not hover-only like the moderation actions:
              // hover does not exist on a phone, and this is the one action
              // every viewer has on every message.
              <button
                type='button'
                className='room-chat__message-reply'
                onClick={handleReply}
                title={`Reply to ${account.display_name || account.username}`}
                aria-label={`Reply to ${account.display_name || account.username}`}
                data-testid='chat-reply-button'
              >
                <svg
                  viewBox='0 0 24 24'
                  width='14'
                  height='14'
                  aria-hidden='true'
                  focusable='false'
                >
                  <path
                    fill='currentColor'
                    d='M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z'
                  />
                </svg>
              </button>
            )}
          </div>
          {showTimeoutMenu && (canTimeout || canBan) && (
            <div className='room-chat__timeout-menu'>
              {canTimeout && (
                <>
                  <span>Timeout for:</span>
                  {TIMEOUT_CHOICES.map((choice) => (
                    <button
                      key={choice.seconds}
                      type='button'
                      className='room-chat__timeout-choice'
                      data-seconds={choice.seconds}
                      onClick={handleTimeout}
                      aria-label={`Timeout for ${choice.label}`}
                    >
                      {choice.label}
                    </button>
                  ))}
                </>
              )}
              {canBan && (
                <button
                  type='button'
                  className='room-chat__timeout-choice room-chat__timeout-choice--ban'
                  onClick={handleBan}
                  aria-label={`Ban ${account.display_name || account.username} from chat`}
                  data-testid='chat-ban-button'
                >
                  Ban
                </button>
              )}
            </div>
          )}
          {content && (
            <div className='room-chat__message-content'>
              {renderWithMentions(
                content,
                emotes ?? [],
                knownHandles,
                selfHandles,
              )}
            </div>
          )}
          {media_attachment && (
            <div className='room-chat__message-media'>
              {media_attachment.type === 'image' && (
                <a
                  href={media_attachment.url}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <img
                    src={media_attachment.preview_url || media_attachment.url}
                    alt='Shared media'
                    className='room-chat__message-image'
                  />
                </a>
              )}
              {media_attachment.type === 'gifv' && (
                // Uploaded GIFs are transcoded to soundless MP4 ("gifv") —
                // rendering them through the <img> branch showed only a
                // frozen thumbnail. Play them like timelines do: looping,
                // muted, inline (muted keeps autoplay allowed everywhere).
                <video
                  src={media_attachment.url}
                  poster={media_attachment.preview_url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className='room-chat__message-image'
                  data-testid='chat-gifv'
                />
              )}
              {media_attachment.type === 'video' && (
                <video
                  src={media_attachment.url}
                  controls
                  className='room-chat__message-video'
                />
              )}
              {media_attachment.type === 'audio' && (
                <audio
                  src={media_attachment.url}
                  controls
                  className='room-chat__message-audio'
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
